import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import { RouterTextLink } from "components/actions/RouterLink";
import Button from "components/ui/button/Button";
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "components/ui/dropdown_menu";
import Tooltip from "components/ui/tooltip/Tooltip";
import { appRoutes } from "lib/appRoutes";
import { DOCUMENTS_AI_FEATURES, useVariableValue } from "lib/featureFlags";
import { useDocumentSummaryCached } from "lib/hooks/api/documents/useDocumentSummaryCached";
import { useCreateLeadOneClick } from "lib/hooks/api/leads/useCreateLeadOneClick";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useURLState } from "lib/hooks/useURLState";
import { EventNames, useTracking } from "lib/tracking";
import { LeadEntityType } from "lib/types/graphQLEnums";
import { CreateLeadFormOutput } from "../leads/create_lead/utils";
import CreateOrViewLeadButton from "../leads/saving/CreateOrViewLeadButton";
import { SaveLeadDrawerProvider, useSaveLeadDrawer } from "../leads/saving/SaveLeadDrawerProvider";
import {
  useViewLeadsDrawer,
  ViewLeadsDrawerProvider,
} from "../leads/viewing/ViewLeadsDrawerProvider";
import { DocumentViewerFiltersSchema } from "./FilterForm";
import { useDocumentTracking } from "./tracking";
import { TAB_LABELS, TabKey, TabLabel } from "./utils";

import css from "./DocumentHeader.module.scss";

const Breadcrumb = styled.div({
  display: "flex",
  alignItems: "center",
  gap: 8,
  minWidth: 0,
  flex: 1,
});

const BuyerName = styled.span(({ theme }) => ({
  maxWidth: 240,
  minWidth: 0,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontSize: 24,
  fontWeight: 700,
  color: theme.colors.sysTextPlaceholder,
}));

const Separator = styled.span(({ theme }) => ({
  fontSize: 24,
  fontWeight: 700,
  color: theme.colors.sysTextPlaceholder,
  flexShrink: 0,
}));

export type DocumentHeaderProps = {
  document: {
    id: string;
    title: string;
    hasBuyerLeads?: boolean;
    orgs: {
      id: string;
      name: string;
    }[];
    leads: {
      id: string;
      name: string;
    }[];
    downloadUrl?: string;
  };
  isLoading: boolean;
  disableTitleLink?: boolean;
  activeTab?: TabKey;
  onTabChange?: (tab: TabKey) => void;
  tabs?: TabLabel[];
};

function DocumentHeaderContent({
  document,
  isLoading,
  disableTitleLink,
  activeTab,
  onTabChange,
  tabs = TAB_LABELS,
}: DocumentHeaderProps) {
  const tracking = useDocumentTracking();
  const currentUser = useAssertCurrentUser();
  const { openLeadDrawer } = useSaveLeadDrawer();
  const [filters] = useURLState("docViewerFilters", { keywords: [] }, DocumentViewerFiltersSchema);

  // Summaries only exist with documents AI on, so skip the read otherwise.
  const isDocumentsAiEnabled = useVariableValue(DOCUMENTS_AI_FEATURES, false);
  const { data: cachedSummary } = useDocumentSummaryCached(document.id, {
    enabled: isDocumentsAiEnabled,
  });
  const summary = cachedSummary?.status === "completed" ? cachedSummary.result?.summary : undefined;

  const docUrl = `/documents/${document.id}`;
  const drawerDefaultValues: Partial<CreateLeadFormOutput> = {
    name: document.title,
    assignedToId: currentUser.guid,
    description: summary
      ? `Lead created from [${document.title}](${docUrl})\n\n${summary}`
      : `Lead created from [${document.title}](${docUrl})`,
    entities: [
      {
        entityId: document.id,
        entityType: LeadEntityType.Document,
      },
      ...document.orgs.map((org) => ({
        entityId: org.id,
        entityType: LeadEntityType.Organisation,
      })),
    ],
  };

  const savedLeadIds = document.leads?.map((lead) => lead.id) || [];

  let buyerLabel: string | undefined;
  if (document.orgs.length === 1) {
    buyerLabel = document.orgs[0].name;
  } else if (document.orgs.length > 1) {
    buyerLabel = "Multiple buyers";
  }
  const buyerTooltip =
    document.orgs.length > 1 ? document.orgs.map((org) => org.name).join(", ") : buyerLabel;

  return (
    <div className={css.header}>
      <Breadcrumb>
        {buyerLabel && (
          <>
            <Tooltip content={buyerTooltip} side="bottom">
              <BuyerName>{buyerLabel}</BuyerName>
            </Tooltip>
            <Separator>/</Separator>
          </>
        )}
        <Tooltip content={document.title} side="bottom">
          <div className={css.ellipsisWrapper}>
            <RouterTextLink
              className={css.title}
              {...appRoutes.documentDetail(document.id)}
              search={{ docViewerFilters: filters }}
              disabled={disableTitleLink}
            >
              {document.title}
            </RouterTextLink>
          </div>
        </Tooltip>
      </Breadcrumb>
      {activeTab && onTabChange ? (
        <>
          <TabsRow aria-label="Document views">
            {tabs.map((tab) => (
              <Button
                key={tab.key}
                aria-pressed={activeTab === tab.key}
                buttonType="primary"
                buttonStyle="inverse"
                disabled={tab.disabled}
                className={activeTab === tab.key ? "active" : undefined}
                onClick={() => onTabChange(tab.key)}
              >
                {tab.label}
              </Button>
            ))}
          </TabsRow>
          <DocumentActionsMenu
            document={document}
            drawerDefaultValues={drawerDefaultValues}
            isLoading={isLoading}
          />
        </>
      ) : (
        <Flex gap={8} alignItems="center">
          <CreateOrViewLeadButton
            savedLeadIds={savedLeadIds}
            entityId={document.id}
            entityType="document"
            hasBuyerLeads={document.hasBuyerLeads ?? false}
            trackingData={{
              "Context source": "Document header",
            }}
            defaultValues={drawerDefaultValues}
            buttonProps={{ disabled: isLoading }}
          />
          <Tooltip content="Save to lead" side="bottom">
            <Button
              onClick={() =>
                openLeadDrawer({
                  entityId: document.id,
                  entityType: "document",
                  defaultValues: drawerDefaultValues,
                })
              }
              icon="bookmark"
              buttonType="secondary"
              aria-label="Save to lead"
              iconProps={{
                fill: "sysTextWhite",
              }}
            />
          </Tooltip>
          {document.downloadUrl ? (
            <Button
              buttonType="secondary"
              as="a"
              href={document.downloadUrl}
              icon="download01"
              aria-label="Download"
              onClick={tracking.exportedData}
            />
          ) : (
            <Tooltip content="No export available">
              {/* Span so the tooltip still triggers while the button inside is disabled */}
              <span>
                <Button
                  buttonType="secondary"
                  disabled={true}
                  icon="download01"
                  aria-label="Download disabled"
                />
              </span>
            </Tooltip>
          )}
        </Flex>
      )}
    </div>
  );
}

type DocumentActionsMenuProps = {
  document: DocumentHeaderProps["document"];
  drawerDefaultValues: Partial<CreateLeadFormOutput>;
  isLoading: boolean;
};

function DocumentActionsMenu({
  document,
  drawerDefaultValues,
  isLoading,
}: DocumentActionsMenuProps) {
  const tracking = useDocumentTracking();
  const { logEvent } = useTracking();
  const { openLeadDrawer } = useSaveLeadDrawer();
  const { openViewLeadsDrawer } = useViewLeadsDrawer();

  const trackingData = { "Context source": "Document header" };

  const { createLeadFromDefaultValues, isLoading: isCreatingLead } = useCreateLeadOneClick({
    defaultValues: drawerDefaultValues,
    trackingData: { ...trackingData, "Data type": "DOCUMENT" },
    onCreateLead: (leadId: string) => {
      logEvent(EventNames.saveDataClicked, {
        ...trackingData,
        "Lead ID": leadId,
        "Data type": "document",
      });
    },
  });

  const savedLeadIds = document.leads?.map((lead) => lead.id) ?? [];
  const isSaved = savedLeadIds.length > 0;
  const hasBuyerLeads = document.hasBuyerLeads ?? false;

  const handleCreateLead = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (hasBuyerLeads) {
      openLeadDrawer({
        entityType: "document",
        entityId: document.id,
        defaultValues: drawerDefaultValues,
        buyerMatchOnly: true,
      });
    } else {
      createLeadFromDefaultValues(e);
    }
  };

  const handleViewMultipleLeads = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    openViewLeadsDrawer({
      savedLeadIds,
      entityType: "document",
    });
  };

  const handleSaveToLead = () => {
    openLeadDrawer({
      entityId: document.id,
      entityType: "document",
      defaultValues: drawerDefaultValues,
    });
  };

  return (
    <DropdownMenu
      trigger={
        <DropdownMenuTrigger
          buttonStyle="inverse"
          icon="dotsHorizontal"
          hideArrow
          buttonType="primary"
          aria-label="Document actions"
        />
      }
      align="end"
      width={200}
      modal={false}
    >
      {renderLeadItem({
        isSaved,
        savedLeadIds,
        isLoading: isLoading || isCreatingLead,
        onCreate: handleCreateLead,
        onViewMultiple: handleViewMultipleLeads,
      })}
      <DropdownMenuItem icon="bookmark" onClick={handleSaveToLead} disabled={isLoading}>
        Save to lead
      </DropdownMenuItem>
      {document.downloadUrl ? (
        <DropdownMenuItem
          as="a"
          href={document.downloadUrl}
          icon="download01"
          onClick={tracking.exportedData}
        >
          Download document
        </DropdownMenuItem>
      ) : (
        <DropdownMenuItem icon="download01" disabled tooltip={{ content: "No export available" }}>
          Download document
        </DropdownMenuItem>
      )}
    </DropdownMenu>
  );
}

function renderLeadItem({
  isSaved,
  savedLeadIds,
  isLoading,
  onCreate,
  onViewMultiple,
}: {
  isSaved: boolean;
  savedLeadIds: string[];
  isLoading: boolean;
  onCreate: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onViewMultiple: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  if (!isSaved) {
    return (
      <DropdownMenuItem icon="target04" onClick={onCreate} disabled={isLoading}>
        Create lead
      </DropdownMenuItem>
    );
  }
  if (savedLeadIds.length === 1) {
    return (
      <DropdownMenuItem as="a" {...appRoutes.leadDetail(savedLeadIds[0])} icon="target04">
        View lead
      </DropdownMenuItem>
    );
  }
  return (
    <DropdownMenuItem icon="target04" onClick={onViewMultiple}>
      View lead
    </DropdownMenuItem>
  );
}

export default function DocumentHeader({
  document,
  isLoading,
  disableTitleLink,
  activeTab,
  onTabChange,
  tabs,
}: DocumentHeaderProps) {
  return (
    <SaveLeadDrawerProvider trackingData={{ "Context source": "Document header" }}>
      <ViewLeadsDrawerProvider>
        <DocumentHeaderContent
          document={document}
          isLoading={isLoading}
          disableTitleLink={disableTitleLink}
          activeTab={activeTab}
          onTabChange={onTabChange}
          tabs={tabs}
        />
      </ViewLeadsDrawerProvider>
    </SaveLeadDrawerProvider>
  );
}

const TabsRow = styled.div({
  display: "flex",
  alignItems: "center",
  gap: 4,
  flexShrink: 0,
});
